import {
  EvidenceAnalysisProvider,
  isNewEvidence,
  isOnlineEvidence,
  useEvidenceAnalysis,
  useNotification,
} from 'features';
import { useTranslation } from 'i18n';
import { logger } from 'monitor';
import { useState } from 'react';
import { Grid, List, ListItem, ListItemText, Page, PhotoFilePicker, Section } from 'ui';

import { ExternalEvidenceForm } from '../../components/ExternalEvidenceForm/ExternalEvidenceForm';

const ExternalEvidenceUpload = () => {
  const { t } = useTranslation('orders');
  const { addEvidence, isLoading, materialEvidenceList } = useEvidenceAnalysis();
  const { open } = useNotification();

  const handleFileUpload = async (files: File[]): Promise<void> => {
    try {
      await Promise.all(files.map((file) => addEvidence({ photo: file })));
      open({
        message: t('externalEvidencePage.upload.notifications.uploadSuccess', { values: { count: files.length } }),
        type: 'success',
      });
    } catch (error) {
      open({
        message: t('externalEvidencePage.upload.notifications.uploadFailed'),
        type: 'error',
      });
      logger.error(t('externalEvidencePage.upload.notifications.uploadFailed'), error);
    }
  };

  return (
    <Section
      title={t('externalEvidencePage.upload.sectionTitle')}
      action={
        <PhotoFilePicker
          onSelectFiles={handleFileUpload}
          multiple={true}
          label={t('externalEvidencePage.upload.actions.uploadEvidence')}
          accept="image/*;"
          slotsProps={{
            buttonProps: {
              loading: isLoading,
            },
          }}
        />
      }
    >
      <List>
        {materialEvidenceList.map((evidence) => {
          const isAnalyzed = isNewEvidence(evidence) || isOnlineEvidence(evidence);
          if (isAnalyzed) {
            const fileName = isNewEvidence(evidence)
              ? evidence.file.name
              : evidence.photo.split('/').at(-1) || evidence.id;

            return (
              <ListItem key={evidence.id}>
                <ListItemText
                  primary={t('externalEvidencePage.upload.values.fileStatus', {
                    values: {
                      fileName,
                      status: evidence.analysisStatus || t('externalEvidencePage.upload.values.loadingStatus'),
                    },
                  })}
                  secondary={evidence.photoRejectedReason ? evidence.photoRejectedReason : ''}
                />
              </ListItem>
            );
          }
          return null;
        })}
      </List>
    </Section>
  );
};
export const ExternalEvidence = () => {
  const { t } = useTranslation('orders');
  const [evidenceId, setEvidenceId] = useState<string | undefined>(undefined);

  return (
    <Page>
      <Page.Title title={t('externalEvidencePage.pageTitle')} />
      <Page.Divider />
      <Grid container>
        <Grid size={12}>
          <Section title={t('externalEvidencePage.sections.evidenceMetadata')}>
            <ExternalEvidenceForm
              onSubmitSuccess={(id) => {
                setEvidenceId(id);
              }}
            />
          </Section>
        </Grid>
        <Grid size={12}>
          {evidenceId ? (
            <EvidenceAnalysisProvider mode="external-evidence" parentId={evidenceId} sopMode="extended">
              <ExternalEvidenceUpload />
            </EvidenceAnalysisProvider>
          ) : null}
        </Grid>
      </Grid>
    </Page>
  );
};
